import React, { FunctionComponent, useContext, useState } from "react";
import Grid from "@material-ui/core/Grid";
import BadgeSummary from "shared/components/BadgeSummary/BadgeSummary";
import Skeleton from "@material-ui/lab/Skeleton";
import styles from "./ProfileOverview.module.scss";
import EndorsementCard from "./EndorsementCard/EndorsementCard";
import AppContext from "context/AppContext";
import apiCalls from "shared/utils/apiCalls";
import Modal from "shared/components/Modal/Modal";
import Input from "shared/components/Input/Input";
import Button from "shared/components/Buttons/Button";
import { AddRounded, DeleteRounded } from "@material-ui/icons";
import CircularProgress from "@material-ui/core/CircularProgress";
import ProfileRecentFeedbacks from "./ProfileRecentFeedbacks/ProfileRecentFeedbacks";

const maxSkills = 6;
const defaultSkills = ["Mentoring"];
const suggestionSkills = [
  "Javascipt",
  "HTML/CSS",
  "Responsive Design",
  "React.js",
  "Vue.js",
  "Angular.js",
  "Node.js",
  "Git",
];

const ProfileOverview: FunctionComponent<{
  userBadges: any;
  skills: any;
  getUserSkills: any;
  gettingSkills: boolean;
  user: any;
  recentFeedbacks: any;
}> = ({
  userBadges,
  skills,
  getUserSkills,
  user,
  gettingSkills,
  recentFeedbacks,
}) => {
  const { state, dispatch } = useContext(AppContext);
  const [skillName, setSkillName] = useState<string>();

  const [createSkillLoading, setCreatingSkillLoading] = useState<boolean>();
  const [addSkillModalOpen, setAddSkillModalOpen] = useState<boolean>(false);
  const [editSkillModalOpen, setEditSkillModalOpen] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<string | undefined>();
  const [endorsingId, setEndorsingId] = useState<string | undefined>();

  const otherSkills = skills.filter(
    ({ name }: { name: string }) => !defaultSkills.includes(name)
  );

  const filteredSuggestionSkills = suggestionSkills.filter(
    (skill) =>
      !skills.map(({ name }: any) => name).includes(skill) &&
      skill !== skillName
  );

  const skillAlreadyExist = skills
    .map(({ name }: any) => name)
    .includes(skillName);

  const skillValidationError = skillName && skillName?.length > 30;

  const hasError = (skillName && skillName?.length > 30) || skillAlreadyExist;

  const getHelperText = () => {
    if (skillValidationError) {
      return "Skill length cannot be more than 30 characters";
    }

    if (skillAlreadyExist) {
      return "Skill already exists";
    }

    return `You can add ${maxSkills - skills.length} more skills`;
  };

  const createSkill = async (skill: string) => {
    if (!state.user) {
      return;
    }

    setCreatingSkillLoading(true);

    const res = await apiCalls(
      {
        method: "post",
        data: { endorsements: [], name: skill },
        url: "/skills",
      },
      dispatch
    );

    if (res) {
      getUserSkills(user.uid);
      setSkillName("");
    }

    setAddSkillModalOpen(false);
    setCreatingSkillLoading(false);
  };

  const addNewSkill = (e: any) => {
    e.preventDefault();

    if (!skillName) {
      return;
    }

    if (hasError) {
      return;
    }

    createSkill(skillName);
  };

  const endorse = async (skillId: string) => {
    setEndorsingId(skillId);
    const res = await apiCalls(
      {
        method: "post",
        url: `/skills/${skillId}`,
      },
      dispatch
    );

    if (res) {
      getUserSkills(user.uid);
    }

    setEndorsingId(undefined);
  };

  const createAndEndorse = async (name: string) => {
    if (!user) {
      return;
    }

    setEndorsingId("Mentoring");

    const res = await apiCalls(
      {
        method: "post",
        data: {
          uid: user.uid,
          name,
        },
        url: `/skills/endorse`,
      },
      dispatch
    );

    if (res) {
      getUserSkills(user.uid);
    }

    setEndorsingId(undefined);
  };

  const mentoringSkill = skills.find(
    ({ name }: { name: string }) => name === "Mentoring"
  );

  const deleteSkill = async (id: string) => {
    setDeletingId(id);
    const res = await apiCalls(
      {
        method: "delete",
        url: `/skills/${id}`,
      },
      dispatch
    );

    if (res) {
      getUserSkills(user.uid);
    }

    setDeletingId(undefined);
  };

  return (
    <div className={styles.profileOverview}>
      <Modal
        open={addSkillModalOpen}
        closeModal={() => setAddSkillModalOpen(false)}
        size="sm"
        className={styles.profileOverview_skillsAndEndorsements_modal}
      >
        <h4 style={{ marginBottom: "var(--space-2xs)" }}>Add skills</h4>

        <form
          onSubmit={addNewSkill}
          className={
            styles.profileOverview_skillsAndEndorsements_modal_addSkill
          }
        >
          <Input
            InputLabelProps={{ shrink: true }}
            name="skill"
            label="Skill"
            variant="outlined"
            fullWidth
            margin="normal"
            autoFocus
            value={skillName}
            onChange={(e: any) => setSkillName(e.target.value)}
            error={hasError}
            helperText={getHelperText()}
          />

          <div
            className={
              styles.profileOverview_skillsAndEndorsements_modal_suggestions
            }
          >
            <div
              className={
                styles.profileOverview_skillsAndEndorsements_modal_suggestions_heading
              }
            >
              Suggestions
            </div>
            {filteredSuggestionSkills.map((skill) => (
              <button
                className={
                  styles.profileOverview_skillsAndEndorsements_modal_suggestions_skillCard
                }
                aria-label="suggestion skills"
                key={skill}
                onClick={() => setSkillName(skill)}
              >
                <div
                  className={
                    styles.profileOverview_skillsAndEndorsements_modal_suggestions_skillCard_wrapper
                  }
                >
                  <div
                    className={
                      styles.profileOverview_skillsAndEndorsements_modal_suggestions_skillCard_name
                    }
                  >
                    {skill}
                  </div>{" "}
                  <AddRounded fontSize="inherit" />
                </div>
              </button>
            ))}
          </div>

          <div
            className={
              styles.profileOverview_skillsAndEndorsements_modal_buttonRow
            }
          >
            <Button
              type="submit"
              aria-label="submit"
              buttonVariant="secondary"
              loading={createSkillLoading}
              disabled={!skillName || maxSkills - skills.length <= 0}
            >
              Add
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={editSkillModalOpen}
        closeModal={() => setEditSkillModalOpen(false)}
        size="sm"
        className={styles.profileOverview_skillsAndEndorsements_modal}
      >
        <h4>Edit skills</h4>
        <div>
          {!otherSkills ||
            (otherSkills.length === 0 && (
              <div
                style={{
                  margin: "var(--space-lg) 0",
                  fontFamily: "var(--heading-font-family)",
                  color: "var(--grey-5)",
                  fontWeight: 500,
                }}
              >
                No skills found
              </div>
            ))}

          {otherSkills &&
            otherSkills.map(({ name, id }: any) => (
              <div
                className={
                  styles.profileOverview_skillsAndEndorsements_modal_editCard
                }
                key={id}
              >
                <div
                  className={
                    styles.profileOverview_skillsAndEndorsements_modal_editCard_name
                  }
                >
                  {name}
                </div>
                <button
                  className={
                    styles.profileOverview_skillsAndEndorsements_modal_editCard_deleteButton
                  }
                  onClick={() => deleteSkill(id)}
                  aria-label="delete"
                  disabled={!!(deletingId && deletingId === id)}
                >
                  {deletingId && deletingId === id ? (
                    <CircularProgress size={16} thickness={5} />
                  ) : (
                    <DeleteRounded fontSize="inherit" />
                  )}
                </button>
              </div>
            ))}
        </div>

        <div
          className={
            styles.profileOverview_skillsAndEndorsements_modal_buttonRow
          }
        >
          <Button
            aria-label="done"
            buttonVariant="secondary"
            onClick={() => setEditSkillModalOpen(false)}
          >
            Ok
          </Button>
        </div>
      </Modal>

      {gettingSkills ? (
        <div style={{ marginBottom: "var(--space-lg)" }}>
          <Skeleton height={300} variant="rect" />
        </div>
      ) : (
        <div className={styles.profileOverview_skillsAndEndorsements}>
          <div className={styles.profileOverview_skillsAndEndorsements_heading}>
            <h4>Skills and Endorsements</h4>

            <div style={{ flexGrow: 1 }} />

            {user?.id === state.user?.id && (
              <>
                <Button
                  buttonVariant="default"
                  size="small"
                  textTransform="capitalize"
                  onClick={() => setAddSkillModalOpen(true)}
                  style={{ marginRight: "var(--space-xs)" }}
                >
                  add
                </Button>
                <Button
                  buttonVariant="default"
                  size="small"
                  textTransform="capitalize"
                  onClick={() => setEditSkillModalOpen(true)}
                >
                  edit
                </Button>
              </>
            )}
          </div>

          <div
            className={
              styles.profileOverview_skillsAndEndorsements_defaultSkils
            }
            style={{
              marginBottom:
                otherSkills && otherSkills.length > 0 ? "var(--space-lg)" : 0,
            }}
          >
            <h6
              className={
                styles.profileOverview_skillsAndEndorsements_subheading
              }
            >
              Devchallenges skills
            </h6>

            {!mentoringSkill && (
              <EndorsementCard
                skill="Mentoring"
                endorsements={[]}
                loading={endorsingId === "Mentoring"}
                onClick={() => createAndEndorse("Mentoring")}
                disable={user?.id === state.user?.id || !state.user}
              />
            )}

            {mentoringSkill && (
              <EndorsementCard
                skillId={mentoringSkill.id}
                skill="Mentoring"
                endorsements={mentoringSkill.endorsements}
                onClick={() => endorse(mentoringSkill.id)}
                disable={user?.id === state.user?.id || !state.user}
                loading={endorsingId === mentoringSkill.id}
                endorsed={mentoringSkill.endorsements.includes(state.user?.uid)}
              />
            )}
          </div>

          {otherSkills && otherSkills.length > 0 && (
            <>
              <h6
                className={
                  styles.profileOverview_skillsAndEndorsements_subheading
                }
              >
                Other skills
              </h6>

              <Grid container spacing={2}>
                {otherSkills &&
                  otherSkills.map(
                    ({ name, endorsements, id }: any, index: number) => (
                      <Grid item xs={12} md={6} key={name}>
                        <EndorsementCard
                          skillId={id}
                          disable={user?.id === state.user?.id || !state.user}
                          skill={name}
                          endorsements={endorsements}
                          borderBottom={index < otherSkills.length - 1}
                          onClick={() => endorse(id)}
                          loading={endorsingId === id}
                          endorsed={endorsements.includes(state.user?.uid)}
                        />
                      </Grid>
                    )
                  )}
              </Grid>
            </>
          )}
        </div>
      )}

      {userBadges ? (
        <BadgeSummary badges={userBadges} rows={3} />
      ) : (
        <Grid container spacing={2}>
          {[0, 1, 2, 3].map((number) => (
            <Grid item xs={12} sm={6} lg={3} key={number}>
              <Skeleton key={number} height={400} variant="rect" />
            </Grid>
          ))}
        </Grid>
      )}

      {recentFeedbacks.length > 0 && (
        <ProfileRecentFeedbacks recentFeedbacks={recentFeedbacks} />
      )}
    </div>
  );
};

export default ProfileOverview;
